import React, { useEffect, useState, useMemo } from "react";
import { LanguageContext } from "../context";
import enTranslations from "../locales/en.json";
import finTranslations from "../locales/fin.json";
import invoiceEn from "../../invoice-generator/locales/en.json";
import invoiceFin from "../../invoice-generator/locales/fin.json";

const translations = {
  en: { ...enTranslations, ...invoiceEn },
  fin: { ...finTranslations, ...invoiceFin },
};

interface LanguageProvider {
  children: React.ReactNode;
}

function LanguageProvider({ children }: Readonly<LanguageProvider>) {
  const [currentLang, setCurrentLang] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const savedLang = localStorage.getItem("language");
      if (savedLang && savedLang in translations) {
        return savedLang as keyof typeof translations;
      }
    }
    return "en";
  });
  const [isClient, setIsClient] = useState(() => typeof window !== "undefined");

  useEffect(() => {
    if (typeof window === "undefined") return;

    setIsClient(true);

    const savedLang = localStorage.getItem("language");

    if (savedLang && savedLang in translations) {
      setCurrentLang(savedLang as keyof typeof translations);
    }
  }, []);

  const t = React.useCallback(
    (key: string) => {
      const keys = key.split(".");
      let translation: any =
        translations[currentLang as keyof typeof translations];

      for (const k of keys) {
        if (!translation[k]) return key;
        translation = translation[k];
      }
      return translation;
    },
    [currentLang]
  );

  const changeLang = React.useCallback(
    (newLang: string) => {
      if (newLang in translations && newLang !== currentLang) {
        setCurrentLang(newLang as keyof typeof translations);
        if (typeof window !== "undefined") {
          localStorage.setItem("language", newLang);
        }
      }
    },
    [currentLang]
  );

  const contextValue = useMemo(() => {
    return {
      currentLang,
      changeLang,
      t,
      isClient,
    };
  }, [currentLang, isClient, changeLang, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export { LanguageProvider };
